/**
 * Aceleres: player das figurinhas vetoriais (Lottie) do WhatsApp.
 *
 * Nasceu dentro do balão (bubbles/LottieSticker.vue) e saiu de lá quando a
 * citação de uma resposta também passou a mostrar a figurinha: o player e o
 * cache precisam ser os MESMOS nos dois lugares, senão o mesmo arquivo de
 * ~800 KB seria baixado uma vez para o balão e outra para a citação.
 *
 * O script vem de public/ (o pnpm deste servidor não aceita dependências
 * novas, e assim o arquivo viaja no deploy junto com o resto de public/) e só
 * é carregado quando aparece a primeira figurinha destas.
 */
const PLAYER_URL = '/aceleres-lottie-player.js';
const FRAME_RENDER_TIMEOUT_MS = 10000;

let playerPromise = null;

export const loadLottiePlayer = () => {
  if (window.lottie) return Promise.resolve(window.lottie);

  if (!playerPromise) {
    playerPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = PLAYER_URL;
      script.onload = () => resolve(window.lottie);
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  return playerPromise;
};

// A lista do chat reagrupa as mensagens sozinha (o álbum de figurinhas fecha
// alguns segundos depois de chegarem, ver MessageList.vue) e nisso a bolha é
// recriada. Sem esta tabela, cada recriação baixava e lia de novo o mesmo
// arquivo.
const animations = new Map();

export const fetchLottieAnimation = url => {
  if (!animations.has(url)) {
    animations.set(
      url,
      fetch(url)
        .then(response => response.json())
        .catch(error => {
          animations.delete(url);
          throw error;
        })
    );
  }

  return animations.get(url);
};

const waitForAnimationReady = animation =>
  new Promise((resolve, reject) => {
    let timeoutId;
    let handleReady = null;
    let handleError = null;
    const cleanup = () => {
      window.clearTimeout(timeoutId);
      animation.removeEventListener('DOMLoaded', handleReady);
      animation.removeEventListener('data_failed', handleError);
    };
    handleReady = () => {
      cleanup();
      resolve();
    };
    handleError = () => {
      cleanup();
      reject(new Error('Não foi possível preparar a figurinha vetorial.'));
    };

    animation.addEventListener('DOMLoaded', handleReady);
    animation.addEventListener('data_failed', handleError);
    timeoutId = window.setTimeout(handleError, FRAME_RENDER_TIMEOUT_MS);
    if (animation.isLoaded) handleReady();
  });

const waitForCanvasFrame = () =>
  new Promise(resolve => {
    window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
  });

const visiblePixelCount = canvas => {
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) {
    throw new Error('Não foi possível preparar a figurinha vetorial.');
  }
  const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
  let visiblePixels = 0;

  // Uma amostra a cada quatro pixels é suficiente para escolher o quadro mais
  // preenchido sem percorrer o canvas inteiro várias vezes.
  for (let index = 3; index < pixels.length; index += 16) {
    if (pixels[index] > 0) visiblePixels += 1;
  }

  return visiblePixels;
};

const canvasPngBlob = canvas =>
  new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (blob) {
        resolve(blob);
        return;
      }

      reject(new Error('Não foi possível preparar a figurinha vetorial.'));
    }, 'image/png');
  });

/**
 * Desenha a animação fora da tela e devolve o quadro mais representativo como
 * PNG. O WhatsApp recebe uma figurinha estática em vez do arquivo JSON.
 */
export const renderLottieStickerFrame = async url => {
  const container = document.createElement('div');
  container.className =
    'fixed -left-[10000px] top-0 size-[512px] pointer-events-none opacity-0';
  container.setAttribute('aria-hidden', 'true');
  document.body.appendChild(container);

  let animation;
  try {
    const [player, animationData] = await Promise.all([
      loadLottiePlayer(),
      fetchLottieAnimation(url),
    ]);
    animation = player.loadAnimation({
      container,
      renderer: 'canvas',
      loop: false,
      autoplay: false,
      animationData,
      rendererSettings: { clearCanvas: true },
    });
    animation.setSubframe(false);
    await waitForAnimationReady(animation);

    const lastFrame = Math.max(0, Math.floor(animation.totalFrames) - 1);
    const candidateFrames = [
      ...new Set(
        [0.25, 0.5, 0.75].map(progress => Math.round(lastFrame * progress))
      ),
    ];
    const canvas = container.querySelector('canvas');
    if (!canvas) {
      throw new Error('Não foi possível preparar a figurinha vetorial.');
    }

    const { frame: bestFrame } = await candidateFrames.reduce(
      async (previousResult, frame) => {
        const currentBest = await previousResult;
        animation.goToAndStop(frame, true);
        await waitForCanvasFrame();
        const pixelCount = visiblePixelCount(canvas);

        return pixelCount > currentBest.pixelCount
          ? { frame, pixelCount }
          : currentBest;
      },
      Promise.resolve({ frame: candidateFrames[0] || 0, pixelCount: -1 })
    );

    animation.goToAndStop(bestFrame, true);
    await waitForCanvasFrame();

    const blob = await canvasPngBlob(canvas);
    return blob;
  } catch {
    throw new Error('Não foi possível preparar a figurinha vetorial.');
  } finally {
    animation?.destroy();
    container.remove();
  }
};

/**
 * Reconhece a figurinha vetorial pelo anexo: ela chega como `application/json`
 * (o desenho é a animação, não uma imagem). Aceita as duas grafias porque a
 * store entrega snake_case e a lista de mensagens passa por `useCamelCase`.
 */
export const isLottieStickerAttachment = attachment => {
  if (!attachment) return false;

  const mime = String(
    attachment.contentType ?? attachment.content_type ?? ''
  ).toLowerCase();
  const url = String(attachment.dataUrl ?? attachment.data_url ?? '');

  return mime === 'application/json' || url.endsWith('.json');
};
